import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material/styles';
import { Grid, InputLabel, FormControl, Select, MenuItem, Typography, Paper, Box, Button } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { API_URL, gridSpacing } from 'utils/constants';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import ApiHelper from 'utils/apiHelper';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AddViewListDialog from './AddViewListDialog';
import EditDeviceColumnsVisibilityDialog from '../Devices/EditDeviceColumnsVisibility';
import { handleDeviceColumns, applyColumnStyle } from '../../../utils/commonHelpers';
import ReportFileExports from 'components/Reports/FileExports';

const ManagementViewsCreate = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [groupId, setGroupId] = useState(0);
    const [deviceTypeId, setDeviceTypeId] = useState(0);
    const [policyId, setPolicyId] = useState(0);
    const [licenseId, setLicenseId] = useState(0);
    const [labelId, setLabelId] = useState(0);
    const [groupsData, setGroupsData] = useState([]);
    const [deviceTypesData, setDeviceTypesData] = useState([]);
    const [policiesData, setPoliciesData] = useState([]);
    const [licensesData, setLicensesData] = useState([]);
    const [labelsData, setLabelsData] = useState([]);
    const [asesetViewsData, setAssetViewsData] = useState([]);
    const [showAddViewListDialog, setShowAddViewListDialog] = useState(false);
    const gridRef = useRef();
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 25
    });
    const [viewsTotalLength, setViewsTotalLength] = useState(0);
    const [selectedRows, setSelectedRows] = useState(new Set()); // Use Set to store unique selected rows
    const [columnDefs, setColumnDefs] = useState([]);
    const [deviceColumnsData, setDeviceColumnsData] = useState([]);
    const [csvDownloadFileHeaders, setCsvDownloadFileHeaders] = useState([]);

    useEffect(() => {
        getGroups();
        getDeviceTypes();
        getDeviceColumns();
        getPolicies();
        getLicenses();
        getLabels();
    }, []);

    useEffect(() => {
        getasesetViewsData({ page: 0, pageSize: paginationModel.pageSize });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId, deviceTypeId, policyId, licenseId, labelId, paginationModel.pageSize]);

    const getGroups = () => {
        ApiHelper.get('/api/reports/groups/')
            .then((res) => {
                setGroupsData(res.data);
            })
            .catch();
    };

    const getDeviceTypes = () => {
        ApiHelper.get('/api/reports/device_types/')
            .then((res) => {
                setDeviceTypesData(res.data);
            })
            .catch();
    };

    const getPolicies = () => {
        ApiHelper.get('/api/reports/policies/')
            .then((res) => {
                setPoliciesData(res.data);
            })
            .catch();
    };

    const getLicenses = () => {
        ApiHelper.get('/api/reports/licenses/')
            .then((res) => {
                setLicensesData(res.data);
            })
            .catch();
    };

    const getLabels = () => {
        ApiHelper.get('/api/reports/labels/')
            .then((res) => {
                setLabelsData(res.data);
            })
            .catch();
    };

    const getasesetViewsData = ({ page, pageSize }) => {
        setLoading(true);
        let label = labelsData.find((data) => data.id === labelId);
        ApiHelper.post(API_URL.MANAGEMENT_VIEWS, {
            group_id: groupId === 0 ? [] : [groupId],
            device_type_id: deviceTypeId === 0 ? [] : [deviceTypeId],
            policy_id: policyId === 0 ? [] : [policyId],
            license_id: licenseId === 0 ? [] : [licenseId],
            label_id: labelId === 0 ? [] : [label.name],
            page_number: page,
            page_size: pageSize
        })
            .then((res) => {
                setAssetViewsData(res.data.data);
                setViewsTotalLength(res.data.total);
                let groupName = 'Any';
                let deviceTypeName = 'Any';
                for (let i = 0; i < groupsData.length; i++) {
                    if (groupsData[i].id === groupId) {
                        groupName = groupsData[i].name;
                    }
                }
                for (let i = 0; i < deviceTypesData.length; i++) {
                    if (deviceTypesData[i].id === deviceTypeId) {
                        deviceTypeName = deviceTypesData[i].name;
                    }
                }
                const temp = [
                    { label: 'Group Name', value: groupName },
                    { label: 'Device Type', value: deviceTypeName }
                ];
                // setFiltersData(temp);
                setLoading(false);
            })
            .catch(() => {
                setAssetViewsData([]);
                toast.warning('There is no view data');
                setLoading(false);
            });
    };

    const getDeviceColumns = () => {
        ApiHelper.get('/api/management/device_columns/2/')
            .then((res) => {
                const [columns_order, columns_visibility] = handleDeviceColumns(res.data);
                setColumnDefs(columns_order);
                setDeviceColumnsData(columns_visibility);
                const temp = [];
                columns_order.map((data) => {
                    temp.push({ label: data.headerName, key: data.field });
                });
                setCsvDownloadFileHeaders(temp);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.warn('There is device columns data');
                }
            });
    };
    const updatePaginationModel = (newPaginationModel) => {
        setPaginationModel(newPaginationModel);
        getasesetViewsData(newPaginationModel);
    };

    const handleSelectionChange = (newSelection) => {
        setSelectedRows((prevSelected) => {
            // Create a new set to avoid modifying the previous state directly
            const updatedSelected = new Set(prevSelected);

            // Add selected rows from the current page
            newSelection.forEach((id) => {
                updatedSelected.add(id);
            });

            // Remove rows that are deselected from the current page
            asesetViewsData.forEach((row) => {
                if (!newSelection.includes(row.deviceid)) {
                    updatedSelected.delete(row.deviceid);
                }
            });
            console.log(updatedSelected);
            return updatedSelected;
        });
    };

    const handleClose = () => {
        if (showAddViewListDialog) setShowAddRuleDialog(false);
        // getRulesData();
    };

    return (
        <MainCard title="View Management" cardHeaderTitle="Device View" cardHeaderShow={true}>
            <Grid container spacing={gridSpacing}>
                <Grid item xs={2} style={{ display: 'flex', flexDirection: 'column' }}>
                    {asesetViewsData.length > 0 && (
                        <div style={{ float: 'right', justifyContent: 'flex-end', paddingBottom: '5px' }}>
                            <ReportFileExports
                                elementId="views"
                                downloadFileName="views"
                                downloadFileOption="csv"
                                data={asesetViewsData}
                                headers={csvDownloadFileHeaders}
                            />
                        </div>
                    )}
                    <SubCard title="Filters">
                        <Grid container>
                            <Grid item xs={12}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select Group</InputLabel>
                                    <Select
                                        label="Select Group"
                                        value={groupId}
                                        name="group"
                                        onChange={(e) => setGroupId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {groupsData.length > 0 &&
                                            groupsData.map((data, key) => (
                                                <MenuItem value={data.id} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select Device Type</InputLabel>
                                    <Select
                                        label="Select Device Type"
                                        value={deviceTypeId}
                                        name="device_type"
                                        onChange={(e) => setDeviceTypeId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {deviceTypesData.length > 0 &&
                                            deviceTypesData.map((data, key) => (
                                                <MenuItem value={data.id} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select Policy</InputLabel>
                                    <Select
                                        label="Select Policy"
                                        value={policyId}
                                        name="policy"
                                        onChange={(e) => setPolicyId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {policiesData.length > 0 &&
                                            policiesData.map((data, key) => (
                                                <MenuItem value={data.id} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select License</InputLabel>
                                    <Select
                                        label="Select License"
                                        value={licenseId}
                                        name="license"
                                        onChange={(e) => setLicenseId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {licensesData.length > 0 &&
                                            licensesData.map((data, key) => (
                                                <MenuItem value={data.id} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select Label</InputLabel>
                                    <Select
                                        label="Select Label"
                                        value={labelId}
                                        name="label"
                                        onChange={(e) => setLabelId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {labelsData.length > 0 &&
                                            labelsData.map((data, key) => (
                                                <MenuItem value={data.id} key={key} data-name={data.name}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <LoadingButton
                                    color="primary"
                                    variant="contained"
                                    sx={{ width: '100%' }}
                                    loading={loading}
                                    onClick={() => setShowAddViewListDialog(true)}
                                >
                                    Save as View
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </SubCard>
                    {showAddViewListDialog && (
                        <AddViewListDialog
                            onClose={handleClose}
                            onCloseCancel={() => setShowAddViewListDialog(false)}
                            selectedFilters={{
                                group: {
                                    id: groupId,
                                    name: groupId ? groupsData.find((g) => g.id === groupId)?.name : 'Any'
                                },
                                deviceType: {
                                    id: deviceTypeId,
                                    name: deviceTypeId ? deviceTypesData.find((dt) => dt.id === deviceTypeId)?.name : 'Any'
                                },
                                policy: {
                                    id: policyId,
                                    name: policyId ? policiesData.find((p) => p.id === policyId)?.name : 'Any'
                                },
                                license: {
                                    id: licenseId,
                                    name: licenseId ? licensesData.find((l) => l.id === licenseId)?.name : 'Any'
                                },
                                label: {
                                    id: labelId,
                                    name: labelId ? labelsData.find((l) => l.id === labelId)?.name : 'Any'
                                }
                            }}
                        />
                    )}
                </Grid>
                <Grid item xs={10}>
                    <Paper id="assetViews">
                        {!loading && asesetViewsData.length === 0 ? (
                            <Typography sx={{ textAlign: 'center', mt: 3 }} color={theme.palette.secondary.main}>
                                No data for views
                            </Typography>
                        ) : (
                            <Box
                                id="asset_devices"
                                sx={{
                                    fontSize: '12px',
                                    '& .device-column-header': {
                                        color: 'black',
                                        fontWeight: 'bold'
                                    },
                                    height: 'calc(100vh - 248px)',
                                    width: '100%'
                                }}
                            >
                                <DataGridPro
                                    columns={columnDefs}
                                    rows={asesetViewsData}
                                    rowCount={viewsTotalLength}
                                    rowHeight={30}
                                    ref={gridRef}
                                    getRowId={(row) => row.deviceid}
                                    pagination
                                    initialState={{
                                        pagination: {
                                            paginationModel
                                        }
                                    }}
                                    pageSizeOptions={[25, 100, 250, 500, 1000]}
                                    checkboxSelection
                                    disableColumnMenu
                                    slotProps={{
                                        pagination: {
                                            showFirstButton: true,
                                            showLastButton: true
                                        }
                                    }}
                                    onRowSelectionModelChange={handleSelectionChange}
                                    sortingMode="server"
                                    // sortModel={sortModel}
                                    paginationMode={'server'}
                                    rowSelectionModel={Array.from(selectedRows)}
                                    loading={loading}
                                    onPaginationModelChange={updatePaginationModel}
                                    disableRowHeightAdjustment={false}
                                    getRowHeight={() => 30}
                                    sx={{
                                        '& .MuiDataGrid-columnHeaders': {
                                            backgroundColor: '#f5f5f5', // Optional: Customize header background
                                            position: 'sticky', // Ensures headers are sticky
                                            top: 0,
                                            zIndex: 1 // Ensures headers stay above content when scrolling
                                        },
                                        '& .MuiDataGrid-virtualScroller': {
                                            overflowY: 'auto',
                                            overflowX: 'auto' // Enable horizontal scrolling
                                        },
                                        '& .MuiDataGrid-columnHeaderTitleContainer': {
                                            display: 'flex',
                                            justifyContent: 'center', // Center text horizontally
                                            alignItems: 'center', // Center text vertically
                                            height: '100%'
                                        }
                                    }}
                                />
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </MainCard>
    );
};

export default ManagementViewsCreate;
